import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow?: ReactNode
    title: ReactNode
    description?: ReactNode
    action?: ReactNode
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    className?: string
    id?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    action,
    align = 'left',
    tone = 'light',
    className,
    id
}): ReactNode => {
    const centered = align === 'center'
    const dark = tone === 'dark'

    return (
        <div className={cn('flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between', centered && 'items-center text-center sm:flex-col sm:items-center', className)}>
            <div className={cn('max-w-2xl', centered && 'mx-auto')}>
                {eyebrow && (
                    <span className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border-2 px-3.5 py-1 font-heading text-sm font-semibold',
                        dark ? 'border-brand-yellow/60 bg-brand-yellow/10 text-brand-yellow' : 'border-brand-choco bg-white text-brand-choco shadow-pop-sm'
                    )}>
                        {eyebrow}
                    </span>
                )}
                <h2 id={id} className={cn('mt-4 font-heading text-3xl font-bold leading-tight sm:text-4xl lg:text-[44px]', dark ? 'text-brand-cream' : 'text-brand-choco')}>
                    {title}
                </h2>
                {description && (
                    <p className={cn('mt-3 text-base leading-relaxed sm:text-lg', dark ? 'text-brand-cream/75' : 'text-muted-foreground')}>
                        {description}
                    </p>
                )}
            </div>
            {action && <div className='shrink-0'>{action}</div>}
        </div>
    )
}
